import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import {
  MAX_PARTY_SIZE,
  MINUTE_CHIPS,
  PARTY_CHIPS,
  draftIsComplete,
  emptyDraft,
  isBehind,
  quickHours,
  settleTypedTime,
  timeAtHour,
  toDateInput,
  toReservedForIso,
  typedTime,
  type NewReservationDraft,
} from './reservationQuickEntry';

interface Props {
  open: boolean;
  nowMs: number;
  onClose: () => void;
  /** Résout sur un message d'erreur à afficher, ou `null` si c'est passé. */
  onSubmit: (draft: NewReservationDraft, reservedForIso: string) => Promise<string | null>;
  /** SC-1281 — présent : on MODIFIE une réservation, le formulaire s'ouvre
   *  sur ses valeurs. Absent : nouvelle réservation. */
  initial?: NewReservationDraft | null;
}

const CHIP = 'shrink-0 h-11 min-w-11 px-3 rounded-full border-2 font-sans text-base font-semibold';
const CHIP_ON = 'border-ink bg-ink text-paper';
const CHIP_OFF = 'border-hairline bg-paper text-ink';
// `shrink-0` sur les champs ET les rangées de pastilles : le clavier réduit
// le 100dvh de la Modal, et une rangée `overflow-x-auto` a une hauteur mini
// de 0 en flex — elle s'écrasait au lieu de laisser défiler le formulaire.
const FIELD = 'shrink-0 h-12 w-full rounded-btn border-2 border-hairline bg-paper px-3 font-sans text-base text-ink';
// SC-1127 — `-mb-2` : le libellé se colle à son champ, plus près que du groupe
// d'avant. SC-1281 — 8 px libellé→champ et 16 px entre groupes (`gap-4`) : à
// 4/12 la fiche paraissait tassée. Le bouton du bas ne défile pas, il reste à
// l'écran quelle que soit la hauteur des champs.
const LABEL = '-mb-2 font-sans text-sm font-bold text-ink-mute';

/**
 * SC-971 — « le client appelle ». Un formulaire à remplir PENDANT l'appel :
 * le jour en un geste, l'heure et le nombre de personnes en pastilles, et le
 * champ libre juste à côté pour tout ce que les pastilles ne couvrent pas.
 *
 * Seuls le nom et le nombre de personnes sont exigés. Au téléphone on n'a
 * souvent qu'un prénom, et réclamer un e-mail reviendrait à en faire inventer
 * un — hub ne le demande pas non plus.
 */
export default function NewReservationSheet({ open, nowMs, onClose, onSubmit, initial = null }: Props) {
  const { t } = useTranslation();
  const editing = initial !== null;
  const title = t(editing ? 'waiter.notes.reservations.editTitle' : 'waiter.notes.reservations.newTitle');
  const [draft, setDraft] = useState<NewReservationDraft>(() => initial ?? emptyDraft(nowMs));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `nowMs` bat pendant que le formulaire est ouvert. Les raccourcis sont
  // figés au moment de l'OUVERTURE, sinon les pastilles se renumérotent sous
  // le doigt : un serveur qui commence à taper à 19h29 verrait « 19:30 »
  // devenir « 20:00 » entre deux mots, et « Aujourd'hui » changer de sens à
  // minuit passé.
  const [openedAtMs, setOpenedAtMs] = useState(nowMs);
  // SC-1127 — ce que le serveur est en train de taper dans le champ heure,
  // tant que ce n'est pas encore une heure. `null` : le champ montre
  // `draft.time`, que les pastilles écrivent aussi.
  const [typed, setTyped] = useState<string | null>(null);
  // Le serveur a quitté le champ sur une heure qui n'existe pas (« 77 »).
  const [timeRejected, setTimeRejected] = useState(false);
  const wasOpen = useRef(open);
  // SC-1281 — la rangée des personnes déborde d'un téléphone : une réservation
  // de 10 s'ouvrirait avec le pas à pas hors de l'écran, et le serveur
  // croirait le nombre perdu. On la fait défiler jusqu'à la valeur.
  const partyRow = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setOpenedAtMs(nowMs);
      setDraft(initial ?? emptyDraft(nowMs));
      setTyped(null);
      setTimeRejected(false);
    }
    wasOpen.current = open;
  }, [open, nowMs, initial]);
  useEffect(() => {
    const row = partyRow.current;
    // `draft === initial` : le premier rendu après l'ouverture montre encore
    // le brouillon d'avant ; on attend celui qui montre la réservation, et
    // on ne touche plus à rien une fois que le serveur a changé une valeur.
    if (!open || !initial || draft !== initial || !row) return;
    // Une pastille allumée : jusqu'à elle. Aucune (plus de 8) : tout au bout,
    // pour voir le nombre ET le « + ».
    const chip = row.querySelector<HTMLElement>('[aria-pressed="true"]');
    row.scrollLeft = chip
      ? Math.max(0, chip.offsetLeft + chip.offsetWidth - row.clientWidth)
      : row.scrollWidth;
  }, [open, initial, draft]);

  const hours = useMemo(() => quickHours(draft.date, openedAtMs), [draft.date, openedAtMs]);
  // L'heure que montrent les pastilles : celle du brouillon, ou celle que la
  // frappe en cours vaut déjà (« 20 » → 20:00). Sans ça, taper « 20 » puis
  // toucher « :30 » tomberait sur une pastille encore éteinte.
  const shownTime = draft.time || (typed ? settleTypedTime(typed) : '');
  const [pickedHour, pickedMinute] = shownTime ? shownTime.split(':').map(Number) : [null, null];
  const today = toDateInput(openedAtMs);
  const tomorrow = toDateInput(openedAtMs + 86_400_000);

  const set = (patch: Partial<NewReservationDraft>) => setDraft((d) => ({ ...d, ...patch }));

  const pickTime = (time: string) => {
    setTyped(null);
    setTimeRejected(false);
    set({ time });
  };

  const close = () => {
    setDraft(emptyDraft(openedAtMs));
    setTyped(null);
    setTimeRejected(false);
    setError(null);
    setBusy(false);
    onClose();
  };

  const submit = async () => {
    const reservedFor = toReservedForIso(draft.date, draft.time);
    if (busy || !reservedFor || !draftIsComplete(draft)) return;
    setBusy(true);
    setError(null);
    const message = await onSubmit(draft, reservedFor);
    if (message === null) {
      close();
    } else {
      setError(message);
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={close} ariaLabel={title} instant>
      {/* SC-1127 — le titre et le bouton du bas ne défilent pas : seuls les
          champs défilent entre les deux. Sur un petit téléphone les rangées
          d'heures poussaient « Enregistrer » sous le bord de l'écran. */}
      <div className="shrink-0 px-4 pt-4 flex items-start justify-between gap-2">
        <h2 className="font-sans text-[18px] font-bold">{title}</h2>
        <button
          type="button"
          data-testid="new-reservation-close"
          aria-label={t('common.close')}
          onClick={close}
          className="-me-2 -mt-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>

      <div data-testid="new-reservation-sheet" data-mode={editing ? 'edit' : 'new'} className="px-4 pt-3 pb-4 flex flex-col gap-4 min-h-0 overflow-y-auto">
        <label className={LABEL} htmlFor="new-resa-name">
          {t('waiter.notes.reservations.fieldName')}
        </label>
        <input
          id="new-resa-name"
          data-testid="new-reservation-name"
          type="text"
          autoComplete="off"
          value={draft.customerName}
          onChange={(e) => set({ customerName: e.target.value })}
          placeholder={t('waiter.notes.reservations.fieldNamePlaceholder')}
          className={FIELD}
        />

        <span className={LABEL}>
          {t('waiter.notes.reservations.fieldParty')}
        </span>
        <div
          ref={partyRow}
          role="group"
          aria-label={t('waiter.notes.reservations.fieldParty')}
          className="relative shrink-0 flex gap-2 overflow-x-auto"
        >
          {PARTY_CHIPS.map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`new-reservation-party-${n}`}
              aria-pressed={draft.partySize === n}
              onClick={() => set({ partySize: n })}
              className={`${CHIP} ${draft.partySize === n ? CHIP_ON : CHIP_OFF}`}
            >
              {n}
            </button>
          ))}
          <span className="shrink-0 flex items-center gap-2">
            <button
              type="button"
              data-testid="new-reservation-party-minus"
              aria-label={t('waiter.notes.reservations.partyMinus')}
              disabled={draft.partySize <= 1}
              onClick={() => set({ partySize: Math.max(1, draft.partySize - 1) })}
              className={`${CHIP} ${CHIP_OFF} disabled:opacity-40`}
            >
              −
            </button>
            <span data-testid="new-reservation-party-value" className="font-sans text-base font-bold tabular-nums">
              {draft.partySize}
            </span>
            <button
              type="button"
              data-testid="new-reservation-party-plus"
              aria-label={t('waiter.notes.reservations.partyPlus')}
              disabled={draft.partySize >= MAX_PARTY_SIZE}
              onClick={() => set({ partySize: Math.min(MAX_PARTY_SIZE, draft.partySize + 1) })}
              className={`${CHIP} ${CHIP_OFF} disabled:opacity-40`}
            >
              +
            </button>
          </span>
        </div>

        <span className={LABEL}>
          {t('waiter.notes.reservations.fieldWhen')}
        </span>
        {/* SC-1281 — le champ date passe à la ligne sur un téléphone étroit
            au lieu d'être rogné (« 10/10/202 »). */}
        <div className="shrink-0 flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="new-reservation-today"
            aria-pressed={draft.date === today}
            onClick={() => set({ date: today })}
            className={`${CHIP} ${draft.date === today ? CHIP_ON : CHIP_OFF}`}
          >
            {t('waiter.notes.reservations.today')}
          </button>
          <button
            type="button"
            data-testid="new-reservation-tomorrow"
            aria-pressed={draft.date === tomorrow}
            onClick={() => set({ date: tomorrow })}
            className={`${CHIP} ${draft.date === tomorrow ? CHIP_ON : CHIP_OFF}`}
          >
            {t('waiter.notes.reservations.tomorrow')}
          </button>
          <input
            data-testid="new-reservation-date"
            type="date"
            aria-label={t('waiter.notes.reservations.fieldWhen')}
            value={draft.date}
            min={today}
            onChange={(e) => set({ date: e.target.value })}
            className={`${FIELD} !w-auto flex-1 min-w-[10rem]`}
          />
        </div>

        {/* SC-1127 — l'heure en deux touches : l'heure, puis les minutes. Le
            champ natif `type="time"` ouvrait l'horloge ronde d'Android ; celui
            d'ici se tape au clavier numérique, pour tout ce que les pastilles
            ne couvrent pas. */}
        <label className={LABEL} htmlFor="new-resa-time">
          {t('waiter.notes.reservations.fieldTime')}
        </label>
        {hours.length > 0 && (
          <div
            role="group"
            aria-label={t('waiter.notes.reservations.timeHours')}
            className="shrink-0 flex flex-wrap gap-x-5 gap-y-2"
          >
            {hours.map((group) => (
              // 44 px et 8 px d'écart (304 px) : les six heures du soir
              // tiennent sur une ligne d'un téléphone de 360 px de large.
              <span key={group[0]} className="flex gap-2">
                {group.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    data-testid={`new-reservation-hour-${hour}`}
                    aria-pressed={pickedHour === hour}
                    onClick={() => pickTime(timeAtHour(draft.date, hour, openedAtMs))}
                    className={`${CHIP} !px-0 w-11 tabular-nums ${pickedHour === hour ? CHIP_ON : CHIP_OFF}`}
                  >
                    {hour}
                  </button>
                ))}
              </span>
            ))}
          </div>
        )}
        <div className="shrink-0 flex flex-wrap items-center gap-2">
          <span role="group" aria-label={t('waiter.notes.reservations.timeMinutes')} className="flex gap-2">
            {MINUTE_CHIPS.map((minute) => {
              const label = String(minute).padStart(2, '0');
              return (
                <button
                  key={minute}
                  type="button"
                  data-testid={`new-reservation-minute-${label}`}
                  aria-pressed={pickedMinute === minute}
                  // Sans heure choisie, les minutes n'ont rien à compléter ; et
                  // « 19:00 » à 19h10 n'est pas un raccourci, c'est déjà passé.
                  disabled={pickedHour === null || isBehind(draft.date, pickedHour, minute, openedAtMs)}
                  onClick={() => pickTime(`${shownTime.slice(0, 2)}:${label}`)}
                  className={`${CHIP} !px-2 tabular-nums disabled:opacity-40 ${pickedMinute === minute ? CHIP_ON : CHIP_OFF}`}
                >
                  :{label}
                </button>
              );
            })}
          </span>
          <input
            id="new-resa-time"
            data-testid="new-reservation-time"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            enterKeyHint="done"
            placeholder="--:--"
            aria-invalid={timeRejected}
            value={typed ?? draft.time}
            // Le champ se vide au toucher : les chiffres tapés remplacent
            // l'heure proposée sans qu'il faille l'effacer. `draft.time` ne
            // bouge pas — quitter le champ sans rien taper la remet.
            onFocus={() => {
              setTyped('');
              setTimeRejected(false);
            }}
            onChange={(e) => {
              const { text, time } = typedTime(e.target.value);
              setTyped(text);
              set({ time });
            }}
            onBlur={() => {
              const settled = typed ? settleTypedTime(typed) : '';
              if (settled) pickTime(settled);
              else if (typed) setTimeRejected(true);
              else setTyped(null);
            }}
            className="shrink-0 ms-2 h-12 w-20 rounded-btn border-2 border-hairline bg-paper px-1 font-sans text-base text-ink text-center tabular-nums aria-[invalid=true]:border-[color:var(--w-danger)]"
          />
        </div>
        {timeRejected && (
          <p data-testid="new-reservation-time-error" role="alert" className="-mt-1 font-sans text-sm font-semibold" style={{ color: 'var(--w-danger)' }}>
            {t('waiter.notes.reservations.timeInvalid')}
          </p>
        )}

        <label className={LABEL} htmlFor="new-resa-phone">
          {t('waiter.notes.reservations.fieldPhone')}
        </label>
        <input
          id="new-resa-phone"
          data-testid="new-reservation-phone"
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={draft.customerPhone}
          onChange={(e) => set({ customerPhone: e.target.value })}
          className={FIELD}
        />

        <label className={LABEL} htmlFor="new-resa-notes">
          {t('waiter.notes.reservations.fieldNotes')}
        </label>
        {/* SC-1281 — sur plusieurs lignes : une note reprise d'une réservation
            (« allergie arachide, poussette, anniversaire… ») doit se relire
            en entier avant d'être corrigée. */}
        <textarea
          id="new-resa-notes"
          data-testid="new-reservation-notes"
          rows={3}
          autoComplete="off"
          value={draft.notes}
          onChange={(e) => set({ notes: e.target.value })}
          className={`${FIELD} !h-auto min-h-12 py-2 resize-none`}
        />
      </div>

      <div className="shrink-0 border-t-2 border-hairline px-4 pt-2 pb-4 flex flex-col gap-3">
        {error && (
          <p data-testid="new-reservation-error" role="alert" className="font-sans text-base font-semibold" style={{ color: 'var(--w-danger)' }}>
            {error}
          </p>
        )}

        <button
          type="button"
          data-testid="new-reservation-submit"
          disabled={busy || !draftIsComplete(draft)}
          aria-busy={busy}
          onClick={() => void submit()}
          className="shrink-0 h-14 rounded-btn bg-ink text-paper font-sans text-[18px] font-bold disabled:opacity-50"
        >
          {t(editing ? 'waiter.notes.reservations.editSubmit' : 'waiter.notes.reservations.newSubmit')}
        </button>
      </div>
    </Modal>
  );
}
